import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
// ANT DESIGN COMPONENTS
import { Alert, Button, Empty, Spin, Tag } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// DATES
import moment from "moment";
// REDUX
import { useLazyGetRolePermissionHistoryQuery } from "../../../redux/roles/roleAPI";
// HOOKS
import useGetPermissionCatalogue from "../../../customHooks/GET_requests/useGetPermissionCatalogue";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// INTERFACES
import type {
  RoleAuditActorKind,
  RolePermissionHistoryEntry,
} from "../../../interfaces/Roles.interface";
// UTILS
import { describeGrant, permissionKeyLabels, type GrantWords } from "./permissionHistoryLabels";

/** Changes read per request; "Load more" reads the next, older page. */
export const PERMISSION_HISTORY_PAGE_SIZE = 20;

const I18N = "organizationManagement.permissionHistory";

/**
 * Read-only history of a role's permission changes (LIFT-1092): when, who, and
 * for each permission that changed its decision before → after. Newest first,
 * paged with LiftCloud's cursor. Permission names come from the server's
 * catalogue; a key it no longer names shows as the raw key.
 *
 * The caller shows this only to users holding roles-permission-history-view;
 * the server enforces the same permission and the role's tenant.
 */
function RolePermissionHistory({ roleId }: { roleId: string }) {
  const [t] = useTranslation("global");
  const { permissionCatalogue } = useGetPermissionCatalogue();
  const labels = useMemo(
    () => permissionKeyLabels(permissionCatalogue?.menu),
    [permissionCatalogue],
  );
  const [fetchHistory, { isFetching }] = useLazyGetRolePermissionHistoryQuery();

  const [entries, setEntries] = useState<RolePermissionHistoryEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  // The role whose history is on screen; a page that arrives for another
  // (the drawer switched roles mid-request) is dropped.
  const activeRole = useRef(roleId);

  const loadPage = useCallback(
    async (cursor: string | null) => {
      const requestedFor = roleId;
      setFailed(false);
      try {
        const token = await GETJwtToken();
        const page = await fetchHistory({
          roleId: requestedFor,
          token,
          limit: PERMISSION_HISTORY_PAGE_SIZE,
          cursor,
        }).unwrap();
        if (activeRole.current !== requestedFor) return;
        setEntries((previous) => (cursor ? [...previous, ...page.entries] : page.entries));
        setNextCursor(page.nextCursor);
      } catch {
        if (activeRole.current === requestedFor) setFailed(true);
      } finally {
        if (activeRole.current === requestedFor) setLoaded(true);
      }
    },
    [roleId, fetchHistory],
  );

  useEffect(() => {
    activeRole.current = roleId;
    setEntries([]);
    setNextCursor(null);
    setLoaded(false);
    if (roleId) loadPage(null);
  }, [roleId, loadPage]);

  const words: GrantWords = {
    undecided: t(`${I18N}.undecided`),
    online: t(`${I18N}.online`),
    offline: t(`${I18N}.offline`),
    on: t(`${I18N}.on`),
    off: t(`${I18N}.off`),
    locked: t(`${I18N}.locked`),
  };

  const actorLabel = (entry: RolePermissionHistoryEntry): string => {
    if (entry.actor_kind === "user") {
      return entry.actor_display_name || t(`${I18N}.unknownUser`);
    }
    const kind = t(`${I18N}.actorKinds.${entry.actor_kind as RoleAuditActorKind}`);
    return entry.source ? `${kind} (${entry.source})` : kind;
  };

  return (
    <div data-testid="role-permission-history">
      <Spin spinning={isFetching} tip={t("general.loading")}>
        {failed && (
          <div data-testid="role-permission-history-error">
            <Alert type="error" showIcon message={t(`${I18N}.loadError`)} />
          </div>
        )}
        {loaded && !failed && entries.length === 0 && (
          <div data-testid="role-permission-history-empty">
            <Empty description={t(`${I18N}.empty`)} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        )}
        {entries.map((entry) => (
          <div
            key={entry.id}
            className="generalStyles__mrFix mb-3"
            data-testid="role-permission-history-entry"
          >
            <div className="generalStyles__flex">
              <span
                className="drawer__text"
                title={t(`${I18N}.time`)}
                data-testid="role-permission-history-time"
              >
                {moment(entry.changed_at).format("YYYY-MM-DD HH:mm:ss")}
              </span>
              <Tag
                className="generalStyles__mlFix"
                title={t(`${I18N}.actor`)}
                data-testid="role-permission-history-actor"
              >
                {actorLabel(entry)}
              </Tag>
            </div>
            <ul className="mb-0" aria-label={t(`${I18N}.changes`)}>
              {entry.changes.map((change) => (
                <li
                  key={change.key}
                  data-testid="role-permission-history-change"
                  data-permission-key={change.key}
                >
                  <strong>{labels.get(change.key) ?? change.key}</strong>
                  {": "}
                  <span data-testid="role-permission-history-before">
                    {describeGrant(change.before, words)}
                  </span>
                  {" → "}
                  <span data-testid="role-permission-history-after">
                    {describeGrant(change.after, words)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {nextCursor && (
          <Button
            size="small"
            onClick={() => loadPage(nextCursor)}
            disabled={isFetching}
            data-testid="role-permission-history-load-more"
          >
            {t(`${I18N}.loadMore`)}
          </Button>
        )}
      </Spin>
    </div>
  );
}

export default RolePermissionHistory;
